import { forwardRef, type FC, type ReactNode } from 'react';
import type { RectReadOnly } from 'src/hooks/use-measure';
import { useMeasure } from 'src/hooks/use-measure';
import { cn } from 'src/utils/cn';

export const addonPadding = '1rem';

const AddonWrapper = forwardRef<
  HTMLDivElement,
  {
    mode?: 'left' | 'right';
    showError?: boolean;
    disabled?: boolean;
    children?: ReactNode;
  }
>(({ mode, showError, disabled, children }, ref) => (
  <div
    ref={ref}
    className={cn(
      `
        pointer-events-none absolute top-0 flex items-center px-m
        not-legacy:border not-legacy:border-transparent not-legacy:p-m
        legacy:h-full legacy:px-[1rem]
      `,
      mode === 'left' && 'left-0',
      mode === 'right' && 'right-0',
      showError &&
        `
          text-foreground-support-error
          legacy:text-legacy-error
        `,
      disabled &&
        `
          cursor-not-allowed text-foreground-surface-disabled
          legacy:text-legacy-inactive
        `
    )}
    data-addon-wrapper
  >
    {children}
  </div>
));

type InnerAddonProps = {
  children: ({
    leftBounds,
    rightBounds,
  }: {
    leftBounds?: RectReadOnly;
    rightBounds?: RectReadOnly;
  }) => ReactNode;
  /**
   * If you need interactivity in your addon, you can wrap your custom UI with `pointer-events: initial;`.
   */
  right?: ReactNode;
  /**
   * If you need interactivity in your addon, you can wrap your custom UI with `pointer-events: initial;`.
   */
  left?: ReactNode;
  showError?: boolean;
  disabled?: boolean;
};

export const InnerAddon: FC<InnerAddonProps> = ({
  children,
  right,
  left,
  showError,
  disabled,
}) => {
  const [refLeft, leftBounds] = useMeasure();
  const [refRight, rightBounds] = useMeasure();

  if (!right && !left) return <>{children({})}</>;

  return (
    <div className="relative">
      {children({
        rightBounds: right ? rightBounds : undefined,
        leftBounds: left ? leftBounds : undefined,
      })}
      {left && (
        <AddonWrapper
          ref={refLeft}
          mode="left"
          showError={showError}
          disabled={disabled}
        >
          {left}
        </AddonWrapper>
      )}

      <AddonWrapper
        ref={refRight}
        mode="right"
        showError={showError}
        disabled={disabled}
      >
        {right}
      </AddonWrapper>
    </div>
  );
};
